'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SAMPLE_SELECTED_FACTS } from '../upload/loading-preview/sample-facts';
import { usePostHog } from 'posthog-js/react';
import { clozeEditError, lectureFileError, normalizeClozeText } from '../../lib/review-validation';
import {
  completeReviewUpload,
  createReviewCardVersion,
  createReviewGenerationJob,
  exportToAnki,
  getCardPreferences,
  getReviewCoverage,
  getReviewQueueSnapshot,
  initializeReviewUpload,
  listReviewCandidates,
  MOCK_GENERATION_DELAY_MS,
  MOCK_GENERATION_ENABLED,
  reviewCandidate,
  reviewCandidateToFlashCard,
  updateReviewReason,
  warmUpApi,
  type FlashCard,
  type CardPreferences,
  type RejectionReason,
  type ReviewCandidate,
  type ReviewCoverage,
  type ReviewDecision,
  type ReviewGenerationJob,
  type ReviewUploadInitStage,
  undoReviewDecision,
  uploadReviewSource,
} from '../lib/api';
import { useTracking } from './TrackingProvider';
import { useAuth } from './AuthProvider';
import { GenerationWaitPanelView } from './GenerationWaitPanelView';
import { UploadLecturePanel } from './UploadLecturePanel';
import { useSubscription } from './SubscriptionProvider';
import { UpgradeModal } from './UpgradeModal';
import { captureError } from '../../lib/error-tracking';
import { saveDeckToLibrary } from '../../lib/auth';
import { getReviewSourceFile } from '../lib/api';
import { isAnkiConnectAvailable, syncCardsToAnki } from '../../lib/anki-connect';
import DeckActions from './DeckActions';
import { DeckCompletionPanel } from './DeckCompletionPanel';
import { downloadDeckPackage } from '@/lib/deck-download';
import { PostExportGrowthPanel } from './PostExportGrowthPanel';
import { TestimonialModal } from './TestimonialModal';
import { useExportCount } from '../hooks/useExportCount';
import { QueueRefillPanel } from './QueueRefillPanel';
import { useExportProgress } from './ToastProvider';
import { RejectionReasonDialog } from './RejectionReasonDialog';
import { NewLectureDialog } from './NewLectureDialog';
import { emptyReviewPhase, factsPreviewRemainingMs, reviewPollInterval, REVIEW_REFILL_THRESHOLD } from '../../lib/review-buffer';
import { localAuthEnabled } from '../../lib/local-auth';
import { useReviewBatchWaitAnalytics } from '../hooks/useReviewBatchWaitAnalytics';


const ACTIVE_JOB_KEY = 'slidetoanki_active_review_job_v1';
const FIRST_CARD_POLL_INTERVAL_MS = 1_000;
const MAX_POLL_INTERVAL_MS = 15_000;
const REVIEW_WORKFLOW_ANALYTICS_VERSION = 'review-workflow-v2';
const DEMO_STEP_DELAY_MS = 10_000;
const DEFAULT_PREFERENCES: CardPreferences = {
  preferred_depth: 'balanced',
  preferred_card_types: ['cloze', 'basic', 'image_occlusion'],
  decision_count: 0,
  accept_rate: null,
  edit_rate: null,
  incorrect_rate: null,
  preferred_generated_type: null,
  guidance: [],
};

type Phase = 'idle' | 'uploading' | 'preparing' | 'reviewing' | 'complete' | 'error';

interface StoredJob {
  jobId: string;
  fileName: string;
  operationId?: string;
  waitStartedAt?: number;
  fileType?: string;
  fileSizeBytes?: number;
  userTier?: string;
  firstCardPresentedAt?: number;
}

interface GenerationWaitContext {
  operationId: string;
  waitStartedAt: number;
  fileType: string;
  fileSizeBytes: number;
  userTier: string;
  jobId?: string;
  firstCardPresented: boolean;
  abandonmentCaptured: boolean;
  backgrounded: boolean;
}

interface LastReview {
  decision: ReviewDecision;
  candidate: ReviewCandidate;
}

interface ReasonPrompt {
  decision: ReviewDecision;
  candidate: ReviewCandidate;
  promptedAt: number;
}

function mockCandidates(jobId: string): ReviewCandidate[] {
  const mockAssetOrigin = typeof window === 'undefined' ? 'http://127.0.0.1:3000' : window.location.origin;
  const cards: Array<ReviewCardVersionShape & {
    type: FlashCard['type'];
    origin: ReviewCandidate['origin'];
  }> = [
    {
      type: 'cloze',
      origin: 'generated',
      cloze_text: 'The {{c1::sinoatrial node}} is the normal pacemaker of the heart.',
      tags: ['cardiology', 'electrophysiology'],
    },
    {
      type: 'basic',
      origin: 'retrieved_exact',
      front: 'Why does hyperkalemia produce peaked T waves?',
      back: 'Elevated extracellular potassium accelerates ventricular repolarization, producing tall, narrow T waves.',
      tags: ['cardiology', 'electrolytes'],
    },
    {
      type: 'cloze',
      origin: 'retrieved_semantic',
      cloze_text: 'Phase 0 depolarization in ventricular myocytes is caused by rapid {{c1::sodium influx}}.',
      tags: ['cardiology', 'action-potential'],
    },
    {
      type: 'basic',
      origin: 'generated',
      front: 'What is the primary effect of beta-1 receptor activation in the heart?',
      back: 'It increases heart rate, conduction velocity, and contractility through increased cAMP.',
      tags: ['cardiology', 'pharmacology'],
    },
    {
      type: 'image_occlusion',
      origin: 'generated',
      image_ref: `${mockAssetOrigin}/mock-cardiac-conduction.svg`,
      occluded_image_ref: `${mockAssetOrigin}/mock-cardiac-conduction-occluded.svg`,
      answer: 'Atrioventricular node',
      hint: 'Which conduction structure is hidden?',
      tags: ['cardiology', 'anatomy'],
    },
  ];
  return cards.map((card, ordinal) => {
    const candidateId = `00000000-0000-4000-8000-${String(ordinal + 1).padStart(12, '0')}`;
    return {
      id: candidateId,
      job_id: jobId,
      source_chunk_id: null,
      canonical_card_id: null,
      origin: card.origin,
      status: 'pending',
      card_type: card.type,
      ordinal,
      source: {
        slide_number: ordinal * 4 + 3,
        excerpt: (card.cloze_text || `${card.front || ''} ${card.back || ''}`)
          .replace(/\{\{c\d+::(.*?)(?:::[^}]*)?\}\}/g, '$1'),
      },
      current_version_id: `${candidateId.slice(0, -1)}${ordinal + 5}`,
      current_version: {
        id: `${candidateId.slice(0, -1)}${ordinal + 5}`,
        candidate_id: candidateId,
        version_number: 1,
        created_by: 'generator',
        front: card.front || null,
        back: card.back || null,
        cloze_text: card.cloze_text || null,
        image_ref: card.image_ref || null,
        occluded_image_ref: card.occluded_image_ref || null,
        answer: card.answer || null,
        hint: card.hint || null,
        tags: card.tags,
      },
    };
  });
}

type ReviewCardVersionShape = {
  front?: string | null;
  back?: string | null;
  cloze_text?: string | null;
  image_ref?: string | null;
  occluded_image_ref?: string | null;
  answer?: string | null;
  hint?: string | null;
  tags: string[];
};

function uuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
}

function elapsedMs(start: string | null | undefined, end: string | null | undefined): number | undefined {
  if (!start || !end) return undefined;
  const value = Date.parse(end) - Date.parse(start);
  return Number.isFinite(value) ? Math.max(0, value) : undefined;
}

function normalizedEditDistance(before: FlashCard, after: FlashCard): number {
  const fields = (card: FlashCard) => [
    card.front,
    card.back,
    card.clozeText,
    card.answer,
    card.hint,
  ].map(value => value || '').join('\u241f');
  const left = fields(before);
  const right = fields(after);
  const longest = Math.max(left.length, right.length);
  if (!longest) return 0;
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let row = 1; row <= left.length; row += 1) {
    const current = [row];
    for (let column = 1; column <= right.length; column += 1) {
      current[column] = Math.min(
        current[column - 1] + 1,
        previous[column] + 1,
        previous[column - 1] + (left[row - 1] === right[column - 1] ? 0 : 1),
      );
    }
    previous = current;
  }
  return Math.min(1, previous[right.length] / longest);
}

function feedbackContext(
  candidate: ReviewCandidate,
  job: ReviewGenerationJob | null,
  userTier: string,
) {
  return {
    job_id: candidate.job_id,
    candidate_id: candidate.id,
    source_chunk_id: candidate.source_chunk_id,
    card_content_hash: candidate.content_hash,
    card_type: candidate.card_type,
    candidate_origin: candidate.origin,
    model_provider: candidate.model_provider,
    model_name: candidate.model_name,
    candidate_prompt_version: candidate.prompt_version,
    generator_version: job?.generator_version,
    job_prompt_version: job?.prompt_version,
    grounding_score: candidate.grounding_score,
    retrieval_score: candidate.retrieval_score,
    quality_score_at_selection: candidate.quality_score_at_selection,
    feedback_schema_version: 'v1',
    user_tier: userTier,
    mock_generation: MOCK_GENERATION_ENABLED,
  };
}

function ClozeText({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  const pattern = /\{\{c\d+::(.*?)(?:::[^}]*)?\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <mark
        key={`${match.index}-${match[1]}`}
        className="rounded-md bg-primary-light px-1.5 py-0.5 font-semibold text-primary ring-1 ring-primary/20"
        title="Clozed answer"
      >
        {match[1]}
      </mark>,
    );
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts.length ? parts : text}</>;
}

function stageLabel(job: Pick<ReviewGenerationJob, 'status' | 'stage'> | null): string {
  if (!job) return 'Preparing your review queue…';
  if (job.stage === 'planning') return 'Finding the highest-yield facts…';
  if (job.stage === 'rendering') return 'Turning selected facts into cards…';
  if (job.stage === 'image_occlusion' || job.stage === 'image_occlusion_due') return 'Building image occlusion cards…';
  switch (job.status) {
    case 'queued': return 'Waiting for a generation worker…';
    case 'parsing': return 'Reading your lecture…';
    case 'retrieving': return 'Looking for proven cards…';
    case 'generating': return 'Generating the next cards…';
    case 'paused_buffer_full': return 'Generation paused while you review';
    case 'failed_retryable': return 'Recovering from a temporary issue…';
    case 'completed': return 'Generation complete';
    case 'cancelled': return 'Generation stopped';
    default: return 'Preparing your review queue…';
  }
}

function versionAsCard(candidate: ReviewCandidate): FlashCard {
  return reviewCandidateToFlashCard(candidate);
}

export function ReviewQueueExperience({ demoFileName }: { demoFileName?: string } = {}) {
  const { runExport, exporting: exportInProgress } = useExportProgress();
  const isDemo = Boolean(demoFileName && localAuthEnabled());
  const mockGenerationEnabled = MOCK_GENERATION_ENABLED || isDemo;
  const posthog = usePostHog();
  const { deviceId } = useTracking();
  const { user, openSignIn } = useAuth();
  const { canGenerate, canSaveToLibrary, deckCount, tier, generationsRemaining, refreshSubscription } = useSubscription();
  const {
    incrementExportCount,
    testimonialGiven,
    setTestimonialGiven,
  } = useExportCount();

  const [phase, setPhase] = useState<Phase>('idle');
  const [job, setJob] = useState<ReviewGenerationJob | null>(null);
  const [pending, setPending] = useState<ReviewCandidate[]>([]);
  const [mockAccepted, setMockAccepted] = useState<ReviewCandidate[]>([]);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [connectionNotice, setConnectionNotice] = useState('');
  const [editing, setEditing] = useState(false);
  const [editCard, setEditCard] = useState<FlashCard | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savingLibrary, setSavingLibrary] = useState(false);
  const [savedLibrary, setSavedLibrary] = useState(false);
  const [lastReview, setLastReview] = useState<LastReview | null>(null);
  const [reasonPrompt, setReasonPrompt] = useState<ReasonPrompt | null>(null);
  const [reasonSubmitting, setReasonSubmitting] = useState(false);
  const [reasonError, setReasonError] = useState('');
  const [showNewLectureDialog, setShowNewLectureDialog] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeTrigger, setUpgradeTrigger] = useState<'generation_limit' | 'library_limit' | 'post_export'>('generation_limit');
  const [postExport, setPostExport] = useState<{ jobId: string; acceptedCards: number } | null>(null);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [waitStartedAt, setWaitStartedAt] = useState<number | null>(null);
  const [coverage, setCoverage] = useState<ReviewCoverage | null>(null);
  const [preferences, setPreferences] = useState<CardPreferences>(DEFAULT_PREFERENCES);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [factsPreviewDeadline, setFactsPreviewDeadline] = useState<{ jobId: string; until: number } | null>(null);

  const presentedAtRef = useRef(new Date().toISOString());
  const sequenceRef = useRef(0);
  const refreshingRef = useRef(false);
  const reviewEpochRef = useRef(0);
  const pollErrorCapturedRef = useRef(false);
  const consecutivePollFailuresRef = useRef(0);
  const pollDelayRef = useRef(FIRST_CARD_POLL_INTERVAL_MS);
  const waitContextRef = useRef<GenerationWaitContext | null>(null);
  const firstCardCapturedJobsRef = useRef(new Set<string>());
  const firstCardCapturePendingJobsRef = useRef(new Set<string>());
  const courseReviewOpenedJobsRef = useRef(new Set<string>());
  const coverageViewsCapturedRef = useRef(new Set<string>());
  const mockAutostartRef = useRef(false);
  const factsFirstShownRef = useRef<{ jobId: string; at: number } | null>(null);
  const current = pending[0] || null;
  const queueRunningLow = pending.length <= REVIEW_REFILL_THRESHOLD;

  // The file picker can leave a phone viewport scrolled to the old upload button.
  // Reset when the screen changes so the wait state and first card start in view.
  useEffect(() => {
    if (phase !== 'idle') window.scrollTo({ top: 0, behavior: 'auto' });
  }, [phase, isDemo, mockGenerationEnabled, demoFileName]);

  const persistActiveJob = useCallback((stored: StoredJob | null) => {
    if (isDemo) return;
    if (stored) localStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify(stored));
    else localStorage.removeItem(ACTIVE_JOB_KEY);
  }, [isDemo]);

  const markActiveJobFirstPresented = useCallback((jobId: string, presentedAt: number) => {
    if (isDemo) return;
    const raw = localStorage.getItem(ACTIVE_JOB_KEY);
    if (!raw) return;
    try {
      const stored = JSON.parse(raw) as StoredJob;
      if (stored.jobId !== jobId) return;
      localStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify({
        ...stored,
        firstCardPresentedAt: presentedAt,
      }));
    } catch {
      // A malformed resume record is handled by the normal resume path.
    }
  }, [isDemo]);

  useEffect(() => {
    if (!deviceId) return;
    let cancelled = false;
    const loadPreferences = async () => {
      try {
        const next = mockGenerationEnabled
          ? JSON.parse(localStorage.getItem('slidetoanki_mock_preferences') || 'null') as CardPreferences | null
          : await getCardPreferences();
        if (!cancelled && next) setPreferences({ ...DEFAULT_PREFERENCES, ...next });
      } catch {
        // Generation remains usable if preference loading is temporarily unavailable.
      }
    };
    void loadPreferences();
    return () => { cancelled = true; };
  }, [deviceId, isDemo, mockGenerationEnabled, demoFileName]);

  const refreshCoverage = useCallback(async (jobId: string) => {
    try {
      if (mockGenerationEnabled) {
        const slides = mockCandidates(jobId).map(candidate => ({
          slide_number: candidate.source?.slide_number || 1,
          excerpt: candidate.source?.excerpt || '',
          planned_facts: 1,
          candidates: 1,
          accepted: mockAccepted.some(item => item.id === candidate.id) ? 1 : 0,
          reviewed: pending.some(item => item.id === candidate.id) ? 0 : 1,
        }));
        setCoverage({
          total_slides: 42,
          content_slides: 34,
          represented_slides: slides.length,
          planned_facts: slides.length,
          candidates: slides.length,
          accepted: mockAccepted.length,
          coverage_percent: Math.round(slides.length / 34 * 100),
          slides,
        });
      } else {
        setCoverage(await getReviewCoverage(jobId));
      }
    } catch {
      // Coverage is supplementary; do not interrupt the review workflow.
    }
  }, [mockAccepted, pending, mockGenerationEnabled]);

  const refreshJob = useCallback(async (jobId: string): Promise<boolean> => {
    if (refreshingRef.current || !deviceId) return false;
    refreshingRef.current = true;
    const epoch = reviewEpochRef.current;
    try {
      const { job: nextJob, candidates } = await getReviewQueueSnapshot(jobId);
      if (epoch !== reviewEpochRef.current) return false;
      setJob(nextJob);
      if (nextJob.course_id && !courseReviewOpenedJobsRef.current.has(nextJob.id)) {
        courseReviewOpenedJobsRef.current.add(nextJob.id);
        posthog?.capture('course_lecture_review_opened', {
          course_id: nextJob.course_id,
          job_id: nextJob.id,
          job_status: nextJob.status,
          pending_count: candidates.length,
          accepted_count: nextJob.counts.accepted,
          reviewed_count: nextJob.counts.reviewed,
          mock_generation: false,
          workflow_schema_version: REVIEW_WORKFLOW_ANALYTICS_VERSION,
        });
      }
      setPending(previous => {
        const existingById = new Map(previous.map(candidate => [candidate.id, candidate]));
        return candidates.map(candidate => {
          const existing = existingById.get(candidate.id);
          return existing?.current_version_id === candidate.current_version_id
            ? existing
            : candidate;
        });
      });
      sequenceRef.current = Math.max(sequenceRef.current, nextJob.counts.reviewed);
      if (pollErrorCapturedRef.current) {
        pollErrorCapturedRef.current = false;
      }
      consecutivePollFailuresRef.current = 0;
      pollDelayRef.current = reviewPollInterval(candidates.length);
      setConnectionNotice('');

      const now = Date.now();
      const hasFacts = Boolean(nextJob.text_parse_completed_at && nextJob.selected_facts?.length);
      if (hasFacts && factsFirstShownRef.current?.jobId !== nextJob.id) {
        factsFirstShownRef.current = { jobId: nextJob.id, at: now };
      }
      const factsHoldMs = factsPreviewRemainingMs({
        hasFacts,
        alreadyPresented: firstCardCapturedJobsRef.current.has(nextJob.id) || Boolean(waitContextRef.current?.jobId === nextJob.id && waitContextRef.current.firstCardPresented),
        reviewedCount: nextJob.counts.reviewed,
        firstShownAt: factsFirstShownRef.current?.jobId === nextJob.id ? factsFirstShownRef.current.at : null,
        now,
      });

      if (candidates.length > 0) {
        if (factsHoldMs > 0) {
          const until = now + factsHoldMs;
          setFactsPreviewDeadline(previous => previous?.jobId === nextJob.id && previous.until === until ? previous : { jobId: nextJob.id, until });
          setPhase('preparing');
        } else {
          setFactsPreviewDeadline(null);
          setPhase('reviewing');
        }
      } else if (nextJob.status === 'completed' || nextJob.status === 'cancelled') {
        setPhase('complete');
      } else if (nextJob.status === 'failed_terminal') {
        const context = waitContextRef.current;
        if (context && !context.firstCardPresented) {
          posthog?.capture('generation_wait_failed', {
            job_id: nextJob.id,
            operation_id: context.operationId,
            elapsed_ms: Math.max(0, Date.now() - context.waitStartedAt),
            stage: nextJob.stage,
            error_code: nextJob.last_error_code,
          });
          waitContextRef.current = null;
          setWaitStartedAt(null);
        }
        setError(nextJob.last_error_message || 'Generation could not be completed.');
        setPhase('error');
      } else {
        setPhase(emptyReviewPhase(nextJob.status, nextJob.counts.reviewed));
      }
      return true;
    } catch (err) {
      if (epoch !== reviewEpochRef.current) return false;
      const message = err instanceof Error ? err.message : 'Could not resume generation';
      if (message.toLowerCase().includes('not found')) {
        persistActiveJob(null);
        setJob(null);
        setPhase('idle');
        return true;
      } else {
        const transient = err instanceof TypeError
          || /failed to fetch|network|load failed|connection/i.test(message);
        if (transient) {
          const isVisible = document.visibilityState === 'visible';
          const isOnline = navigator.onLine;

          // Browsers routinely reject in-flight fetches while suspending or
          // restoring a background tab. Pause until the page is usable again
          // instead of treating that lifecycle transition as a service error.
          if (!isVisible || !isOnline) {
            if (!isOnline) {
              setConnectionNotice('You\u2019re offline. We\u2019ll reconnect automatically.');
            }
            return false;
          }

          consecutivePollFailuresRef.current += 1;
          pollDelayRef.current = Math.min(
            MAX_POLL_INTERVAL_MS,
            FIRST_CARD_POLL_INTERVAL_MS * (2 ** consecutivePollFailuresRef.current),
          );
          // A failed poll already represents three failed read attempts. Only
          // escalate if another visible, online poll also fails, so monitoring
          // reflects sustained user-facing degradation rather than a tab wake-up.
          if (consecutivePollFailuresRef.current >= 2 && !pollErrorCapturedRef.current) {
            captureError('review_queue_poll_error', err, {
              job_id: jobId,
              consecutive_failures: consecutivePollFailuresRef.current,
              retry_in_ms: pollDelayRef.current,
              online: isOnline,
              visibility_state: document.visibilityState,
            });
            pollErrorCapturedRef.current = true;
          }
          if (consecutivePollFailuresRef.current >= 2) {
            setConnectionNotice('Connection interrupted. Reconnecting automatically…');
          }
        } else {
          setError(message);
        }
        return false;
      }
    } finally {
      refreshingRef.current = false;
    }
  }, [deviceId, persistActiveJob, posthog]);

  useEffect(() => {
    if (!factsPreviewDeadline || factsPreviewDeadline.jobId !== job?.id || phase !== 'preparing' || !current) return;
    const timer = window.setTimeout(() => {
      setFactsPreviewDeadline(null);
      setPhase('reviewing');
    }, Math.max(0, factsPreviewDeadline.until - Date.now()));
    return () => window.clearTimeout(timer);
  }, [factsPreviewDeadline, job?.id, phase, current]);

  useEffect(() => {
    if (!deviceId) return;
    if (mockGenerationEnabled) {
      if (!isDemo) localStorage.removeItem(ACTIVE_JOB_KEY);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const linkedJobId = params.get('job');
    const linkedFileName = params.get('file') || 'Lecture';
    const raw = linkedJobId
      ? JSON.stringify({ jobId: linkedJobId, fileName: linkedFileName })
      : localStorage.getItem(ACTIVE_JOB_KEY);
    if (!raw) return;
    let stored: StoredJob;
    try {
      stored = JSON.parse(raw) as StoredJob;
      if (!stored.jobId) return;
    } catch {
      localStorage.removeItem(ACTIVE_JOB_KEY);
      return;
    }
    let cancelled = false;
    let resumeTimer: number | undefined;
    let restoredFromServer = false;

    const restoreLocalState = () => {
      setFileName(stored.fileName);
      if (stored.operationId && stored.waitStartedAt) {
        const wasAlreadyPresented = Boolean(stored.firstCardPresentedAt);
        waitContextRef.current = {
          operationId: stored.operationId,
          waitStartedAt: stored.waitStartedAt,
          fileType: stored.fileType || 'unknown',
          fileSizeBytes: stored.fileSizeBytes || 0,
          userTier: stored.userTier || tier,
          jobId: stored.jobId,
          firstCardPresented: wasAlreadyPresented,
          abandonmentCaptured: false,
          // Preserve an active wait; a restored job has unknown time away.
          backgrounded: waitContextRef.current?.jobId === stored.jobId
            ? waitContextRef.current.backgrounded : true,
        };
        if (wasAlreadyPresented) {
          firstCardCapturedJobsRef.current.add(stored.jobId);
        } else {
          setWaitStartedAt(stored.waitStartedAt);
        }
      }
      setPhase('preparing');
    };
    const clearResumeTimer = () => {
      if (resumeTimer !== undefined) {
        window.clearTimeout(resumeTimer);
        resumeTimer = undefined;
      }
    };
    const resumeWhenUsable = async () => {
      clearResumeTimer();
      if (
        cancelled
        || restoredFromServer
        || document.visibilityState !== 'visible'
        || !navigator.onLine
      ) return;

      const warmup = await warmUpApi();
      if (cancelled || document.visibilityState !== 'visible' || !navigator.onLine) return;
      if (!warmup.success) {
        setConnectionNotice('The server is taking longer than expected. Still reconnecting\u2026');
        resumeTimer = window.setTimeout(resumeWhenUsable, MAX_POLL_INTERVAL_MS);
        return;
      }

      restoredFromServer = await refreshJob(stored.jobId);
      if (!restoredFromServer && !cancelled && document.visibilityState === 'visible' && navigator.onLine) {
        resumeTimer = window.setTimeout(resumeWhenUsable, pollDelayRef.current);
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void resumeWhenUsable();
      else clearResumeTimer();
    };
    const handleOnline = () => void resumeWhenUsable();
    const handleOffline = () => {
      clearResumeTimer();
      setConnectionNotice('You\u2019re offline. We\u2019ll reconnect automatically.');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    resumeTimer = window.setTimeout(() => {
      restoreLocalState();
      if (navigator.onLine) void resumeWhenUsable();
      else handleOffline();
    }, 0);

    return () => {
      cancelled = true;
      clearResumeTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [deviceId, refreshJob, tier, isDemo, mockGenerationEnabled, demoFileName]);

  useEffect(() => {
    if (mockGenerationEnabled || !job?.id || job.status === 'completed' || job.status === 'cancelled' || job.status === 'failed_terminal') {
      return;
    }
    let cancelled = false;
    let timer: number | undefined;

    const canPoll = () => document.visibilityState === 'visible' && navigator.onLine;
    const clearPollTimer = () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timer = undefined;
      }
    };
    const schedulePoll = (poll: () => Promise<void>, delay: number) => {
      clearPollTimer();
      if (!cancelled && canPoll()) {
        timer = window.setTimeout(poll, delay);
      }
    };
    const poll = async () => {
      if (!canPoll()) return;
      await refreshJob(job.id);
      schedulePoll(poll, pollDelayRef.current);
    };
    const resumePolling = async () => {
      clearPollTimer();
      if (cancelled || document.visibilityState !== 'visible') return;
      if (!navigator.onLine) {
        setConnectionNotice('You\u2019re offline. We\u2019ll reconnect automatically.');
        return;
      }

      const warmup = await warmUpApi();
      if (cancelled || document.visibilityState !== 'visible' || !navigator.onLine) return;
      if (!warmup.success) {
        setConnectionNotice('The server is taking longer than expected. Still reconnecting\u2026');
        schedulePoll(poll, MAX_POLL_INTERVAL_MS);
        return;
      }
      await poll();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void resumePolling();
      else clearPollTimer();
    };
    const handleOnline = () => void resumePolling();
    const handleOffline = () => {
      clearPollTimer();
      setConnectionNotice('You\u2019re offline. We\u2019ll reconnect automatically.');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (canPoll()) schedulePoll(poll, queueRunningLow && !consecutivePollFailuresRef.current
      ? FIRST_CARD_POLL_INTERVAL_MS
      : pollDelayRef.current);
    else if (!navigator.onLine) handleOffline();

    return () => {
      cancelled = true;
      clearPollTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [job?.id, job?.status, queueRunningLow, refreshJob, isDemo, mockGenerationEnabled, demoFileName]);

  useEffect(() => {
    if (phase !== 'reviewing') return;
    presentedAtRef.current = new Date().toISOString();
    const resetTimer = window.setTimeout(() => {
      setEditing(false);
      setEditCard(current ? versionAsCard(current) : null);
    }, 0);
    if (current) {
      posthog?.capture('candidate_presented', {
        ...feedbackContext(current, null, tier),
        $insert_id: `candidate-presented:${current.id}`,
      });
    }
    return () => window.clearTimeout(resetTimer);
  }, [current, phase, posthog, tier, isDemo, mockGenerationEnabled, demoFileName]);

  useEffect(() => {
    const waiting = phase === 'uploading' || phase === 'preparing';
    if (!waiting || !waitStartedAt) return;
    const previousTitle = document.title;
    document.title = 'Preparing your first card · SlideToAnki';
    return () => {
      document.title = previousTitle;
    };
  }, [phase, waitStartedAt, isDemo, mockGenerationEnabled, demoFileName]);

  useEffect(() => {
    if (phase !== 'reviewing' || !current || !job) return;
    const context = waitContextRef.current;
    if (context?.firstCardPresented) {
      firstCardCapturedJobsRef.current.add(job.id);
      return;
    }
    if (
      firstCardCapturedJobsRef.current.has(job.id)
      || firstCardCapturePendingJobsRef.current.has(job.id)
    ) return;
    firstCardCapturePendingJobsRef.current.add(job.id);

    const waitStartIso = new Date(
      context?.waitStartedAt || Date.parse(job.created_at),
    ).toISOString();
    const presentedAt = new Date().toISOString();
    if (context) context.firstCardPresented = true;
    markActiveJobFirstPresented(job.id, Date.parse(presentedAt));
    setWaitStartedAt(null);

    void (async () => {
      let timingJob = job;
      if (!mockGenerationEnabled && !job.first_candidate_ready_at) {
        // The candidate insert and the once-only timing checkpoint are two
        // adjacent database operations. Re-read briefly so a poll landing in
        // that tiny gap still emits the complete latency breakdown.
        await new Promise(resolve => window.setTimeout(resolve, 200));
        try {
          timingJob = (await getReviewQueueSnapshot(job.id)).job;
        } catch {
          // The core click-to-card timing remains valid without enrichment.
        }
      }

      const properties: Record<string, unknown> = {
        job_id: timingJob.id,
        candidate_id: current.id,
        operation_id: context?.operationId,
        card_type: current.card_type,
        file_type: context?.fileType,
        file_size_bytes: context?.fileSizeBytes,
        user_tier: context?.userTier || tier,
        source_slide_count: timingJob.source_slide_count,
        mock_generation: mockGenerationEnabled,
        client_time_to_first_card_ms: elapsedMs(waitStartIso, presentedAt),
        first_card_backgrounded: context?.backgrounded ?? true,
        upload_to_job_ms: elapsedMs(waitStartIso, timingJob.created_at),
        queue_ms: elapsedMs(timingJob.created_at, timingJob.started_at),
        text_parse_ms: elapsedMs(timingJob.started_at, timingJob.text_parse_completed_at),
        planning_ms: elapsedMs(timingJob.text_parse_completed_at, timingJob.planning_completed_at),
        backend_time_to_first_card_ms: elapsedMs(timingJob.created_at, timingJob.first_candidate_ready_at),
        ready_to_present_ms: elapsedMs(timingJob.first_candidate_ready_at, presentedAt),
      };
      firstCardCapturedJobsRef.current.add(job.id);
      firstCardCapturePendingJobsRef.current.delete(job.id);
      posthog?.capture('first_card_presented', properties);
    })();
  }, [current, job, markActiveJobFirstPresented, phase, posthog, tier, isDemo, mockGenerationEnabled, demoFileName]);

  useEffect(() => {
    const markBackgrounded = () => {
      const context = waitContextRef.current;
      if (context && (document.visibilityState !== 'visible' || !navigator.onLine)) context.backgrounded = true;
    };
    document.addEventListener('visibilitychange', markBackgrounded);
    window.addEventListener('offline', markBackgrounded);
    const handlePageHide = () => {
      const context = waitContextRef.current;
      if (
        !context
        || context.firstCardPresented
        || context.abandonmentCaptured
        || (phase !== 'uploading' && phase !== 'preparing')
      ) return;
      context.abandonmentCaptured = true;
      posthog?.capture('generation_wait_abandoned', {
        job_id: context.jobId,
        operation_id: context.operationId,
        elapsed_ms: Math.max(0, Date.now() - context.waitStartedAt),
        stage: job?.stage || phase,
        file_type: context.fileType,
        user_tier: context.userTier,
      });
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', markBackgrounded);
      window.removeEventListener('offline', markBackgrounded);
    };
  }, [job?.stage, phase, posthog, isDemo, mockGenerationEnabled, demoFileName]);

  const startJob = useCallback(async (file: File) => {
    if (!deviceId) return;
    const lower = file.name.toLowerCase();
    const fileError = lectureFileError(file);
    if (fileError) {
      setError(fileError);
      setPhase('error');
      return;
    }
    if (!user || user.is_anonymous) {
      void openSignIn('/upload');
      return;
    }
    if (!mockGenerationEnabled && user && !canGenerate) {
      setUpgradeTrigger('generation_limit');
      setShowUpgrade(true);
      return;
    }

    setError('');
    setConnectionNotice('');
    pollErrorCapturedRef.current = false;
    consecutivePollFailuresRef.current = 0;
    pollDelayRef.current = FIRST_CARD_POLL_INTERVAL_MS;
    factsFirstShownRef.current = null;
    setFactsPreviewDeadline(null);
    setFileName(file.name);
    const operationId = uuid();
    const waitStart = Date.now();
    const fileType = lower.split('.').pop() || 'unknown';
    const waitContext: GenerationWaitContext = {
      operationId,
      waitStartedAt: waitStart,
      fileType,
      fileSizeBytes: file.size,
      userTier: tier,
      firstCardPresented: false,
      abandonmentCaptured: false,
      backgrounded: document.visibilityState !== 'visible' || !navigator.onLine,
    };
    waitContextRef.current = waitContext;
    setWaitStartedAt(waitStart);
    setPhase('uploading');
    posthog?.capture('generation_wait_started', {
      operation_id: operationId,
      file_type: fileType,
      file_size_bytes: file.size,
      user_tier: tier,
      mock_generation: mockGenerationEnabled,
    });
    let failureStage: ReviewUploadInitStage | 'mock_generation' | 'uploading_source' | 'completing_upload' | 'creating_job' | 'refreshing_account' | 'loading_job' = mockGenerationEnabled
      ? 'mock_generation'
      : 'warming_api';
    let acknowledgedJob: ReviewGenerationJob | null = null;
    try {
      if (mockGenerationEnabled) {
        const jobId = uuid();
        const candidates = mockCandidates(jobId);
        const firstReadyAt = new Date().toISOString();
        const mockJob: ReviewGenerationJob = {
          id: jobId,
          upload_id: '00000000-0000-4000-8000-000000000098',
          status: 'paused_buffer_full',
          stage: 'generating',
          requested_depth: 'balanced',
          requested_card_types: ['cloze', 'basic', 'image_occlusion'],
          accepted_goal: 20,
          course_id: new URLSearchParams(window.location.search).get('course'),
          preference_snapshot: { ...preferences },
          created_at: new Date(waitStart + 200).toISOString(),
          started_at: new Date(waitStart + 400).toISOString(),
          text_parse_completed_at: new Date(waitStart + 900).toISOString(),
          media_parse_completed_at: new Date(waitStart + 1_400).toISOString(),
          planning_completed_at: new Date(waitStart + 1_800).toISOString(),
          first_candidate_ready_at: firstReadyAt,
          source_slide_count: 42,
          selected_facts: SAMPLE_SELECTED_FACTS,
          counts: {
            pending: candidates.length,
            accepted: 0,
            rejected: 0,
            incorrect: 0,
            reviewed: 0,
            total: candidates.length,
          },
        };
        if (isDemo) {
          const emptyCounts = { pending: 0, accepted: 0, rejected: 0, incorrect: 0, reviewed: 0, total: 0 };
          await new Promise(resolve => window.setTimeout(resolve, DEMO_STEP_DELAY_MS));
          setPhase('preparing');
          setJob({ ...mockJob, status: 'parsing', stage: 'parsing', text_parse_completed_at: null,
            planning_completed_at: null, first_candidate_ready_at: null, selected_facts: [], counts: emptyCounts });
          await new Promise(resolve => window.setTimeout(resolve, DEMO_STEP_DELAY_MS));
          setJob({ ...mockJob, status: 'generating', stage: 'planning', planning_completed_at: null,
            first_candidate_ready_at: null, selected_facts: [], counts: emptyCounts });
          await new Promise(resolve => window.setTimeout(resolve, DEMO_STEP_DELAY_MS));
          setJob({ ...mockJob, status: 'generating', stage: 'generating',
            first_candidate_ready_at: null, counts: emptyCounts });
          await new Promise(resolve => window.setTimeout(resolve, DEMO_STEP_DELAY_MS));
        } else {
          await new Promise(resolve => window.setTimeout(resolve, MOCK_GENERATION_DELAY_MS));
        }
        waitContext.jobId = jobId;
        setJob(mockJob);
        setPending(candidates);
        setMockAccepted([]);
        setPhase('reviewing');
        posthog?.capture('review_generation_started', {
          job_id: jobId,
          operation_id: operationId,
          accepted_goal: mockJob.accepted_goal,
          user_tier: tier,
          file_type: fileType,
          upload_to_job_ms: Math.max(0, Date.now() - waitStart),
          course_id: mockJob.course_id,
          mock_generation: true,
          workflow_schema_version: REVIEW_WORKFLOW_ANALYTICS_VERSION,
        });
        if (mockJob.course_id) {
          courseReviewOpenedJobsRef.current.add(jobId);
          posthog?.capture('course_lecture_review_opened', {
            course_id: mockJob.course_id,
            job_id: jobId,
            job_status: mockJob.status,
            pending_count: candidates.length,
            accepted_count: 0,
            reviewed_count: 0,
            mock_generation: true,
            workflow_schema_version: REVIEW_WORKFLOW_ANALYTICS_VERSION,
          });
        }
        return;
      }

      const upload = await initializeReviewUpload(
        file,
        `upload-${operationId}`,
        stage => { failureStage = stage; },
      );
      failureStage = 'uploading_source';
      await uploadReviewSource(file, upload);
      failureStage = 'completing_upload';
      await completeReviewUpload(upload.id, upload.sha256);

      const depth = tier === 'free' ? 'concise' : 'balanced';
      const acceptedGoal = tier === 'free' ? 15 : (tier === 'student' || tier === 'plus') ? 30 : 45;
      failureStage = 'creating_job';
      const createdJob = await createReviewGenerationJob(upload.id, {
        depth,
        cardTypes: ['cloze', 'basic', 'image_occlusion'],
        acceptedGoal,
        idempotencyKey: `job-${operationId}`,
      });
      acknowledgedJob = createdJob;

      waitContext.jobId = createdJob.id;
      setJob(createdJob);
      setPhase('preparing');
      persistActiveJob({
        jobId: createdJob.id,
        fileName: file.name,
        operationId,
        waitStartedAt: waitStart,
        fileType,
        fileSizeBytes: file.size,
        userTier: tier,
      });
      posthog?.capture('review_generation_started', {
        job_id: createdJob.id,
        operation_id: operationId,
        accepted_goal: acceptedGoal,
        user_tier: tier,
        file_type: fileType,
        upload_to_job_ms: Math.max(0, Date.now() - waitStart),
      });

      if (user) {
        // The API atomically reserves signed-in quota while creating the job.
        failureStage = 'refreshing_account';
        try {
          await refreshSubscription();
        } catch (err) {
          // Account counters are supplementary once generation is running.
          // Do not discard a valid job because that refresh was interrupted.
          captureError('review_queue_subscription_refresh_error', err, {
            job_id: createdJob.id,
            operation_id: operationId,
          });
        }
      }
      failureStage = 'loading_job';
      const refreshed = await refreshJob(createdJob.id);
      if (!refreshed) {
        setConnectionNotice('Generation started. Reconnecting automatically…');
        setPhase('preparing');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not start generation';
      if (acknowledgedJob) {
        captureError('review_queue_post_start_error', err, {
          job_id: acknowledgedJob.id,
          operation_id: operationId,
          failure_stage: failureStage,
        });
        setError('');
        setConnectionNotice('Generation started. Reconnecting automatically…');
        setPhase('preparing');
        return;
      }
      if (/monthly lecture limit reached/i.test(message)) {
        posthog?.capture('generation_wait_failed', {
          operation_id: operationId,
          elapsed_ms: Math.max(0, Date.now() - waitStart),
          file_type: fileType,
          failure_reason: 'generation_limit',
          failure_stage: failureStage,
        });
        waitContextRef.current = null;
        setWaitStartedAt(null);
        await refreshSubscription();
        setPhase('idle');
        setUpgradeTrigger('generation_limit');
        setShowUpgrade(true);
        return;
      }
      captureError('review_queue_start_error', err, {
        file_type: lower.split('.').pop(),
        file_size_mb: Math.round(file.size / 1024 / 1024 * 100) / 100,
        operation_id: operationId,
        failure_stage: failureStage,
      });
      posthog?.capture('generation_wait_failed', {
        job_id: waitContext.jobId,
        operation_id: operationId,
        elapsed_ms: Math.max(0, Date.now() - waitStart),
        file_type: fileType,
        error_message: message,
        failure_stage: failureStage,
      });
      waitContextRef.current = null;
      setWaitStartedAt(null);
      setError(message);
      setPhase('error');
    }
  }, [canGenerate, deviceId, persistActiveJob, posthog, preferences, refreshJob, refreshSubscription, tier, user, openSignIn, mockGenerationEnabled, isDemo]);

  useEffect(() => {
    if (!mockGenerationEnabled || !deviceId || mockAutostartRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const mockFile = isDemo ? demoFileName : params.get('mockFile');
    if (!mockFile) return;
    const extension = mockFile.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    const timer = window.setTimeout(() => {
      mockAutostartRef.current = true;
      void startJob(new File(['SlideToAnki local lecture demo'], mockFile, { type: extension }));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [deviceId, startJob, isDemo, mockGenerationEnabled, demoFileName]);

  useEffect(() => {
    if (!job?.id || phase !== 'complete') return;
    const timer = window.setTimeout(() => void refreshCoverage(job.id), 250);
    return () => window.clearTimeout(timer);
  }, [job?.id, job?.counts.reviewed, phase, refreshCoverage, isDemo, mockGenerationEnabled, demoFileName]);

  useEffect(() => {
    if (!coverage || !job?.id || phase !== 'complete') return;
    const source = 'completion';
    const viewKey = `${job.id}:${source}`;
    if (coverageViewsCapturedRef.current.has(viewKey)) return;
    coverageViewsCapturedRef.current.add(viewKey);
    posthog?.capture('review_coverage_viewed', {
      $insert_id: `review-coverage-viewed:${viewKey}`,
      job_id: job.id,
      course_id: job.course_id,
      source,
      coverage_percent: coverage.coverage_percent,
      represented_slides: coverage.represented_slides,
      content_slides: coverage.content_slides,
      accepted_count: coverage.accepted,
      mock_generation: mockGenerationEnabled,
      workflow_schema_version: REVIEW_WORKFLOW_ANALYTICS_VERSION,
    });
  }, [coverage, job, phase, posthog, isDemo, mockGenerationEnabled, demoFileName]);

  const submitDecision = useCallback(async (
    action: ReviewDecision['action'],
    selectedVersionId?: string | null,
    rejectionReason?: RejectionReason | null,
    candidateOverride?: ReviewCandidate,
  ) => {
    if (!current || submitting || reasonPrompt) return;
    const reviewedCandidate = candidateOverride || current;
    setError('');
    setSyncMessage('');
    setSubmitting(true);
    try {
      const decisionLatencyMs = Math.max(0, Date.now() - Date.parse(presentedAtRef.current));
      const decision: ReviewDecision = mockGenerationEnabled
        ? {
            id: uuid(),
            candidate_id: reviewedCandidate.id,
            selected_version_id: selectedVersionId || reviewedCandidate.current_version_id,
            action,
            rejection_reason: rejectionReason,
            reviewed_at: new Date().toISOString(),
          }
        : await reviewCandidate(reviewedCandidate.id, {
            action,
            selectedVersionId: selectedVersionId || reviewedCandidate.current_version_id,
            rejectionReason,
            presentedAt: presentedAtRef.current,
            decisionLatencyMs,
            clientSequence: ++sequenceRef.current,
            idempotencyKey: `review-${uuid()}`,
          });
      setLastReview({ decision, candidate: reviewedCandidate });
      setPending(previous => previous.filter(candidate => candidate.id !== reviewedCandidate.id));
      if (mockGenerationEnabled && (action === 'accept' || action === 'edit_accept')) {
        setMockAccepted(previous => [...previous, reviewedCandidate]);
      }
      if (job) {
        setJob({
          ...job,
          counts: {
            ...job.counts,
            pending: Math.max(0, job.counts.pending - 1),
            accepted: job.counts.accepted + (action === 'accept' || action === 'edit_accept' ? 1 : 0),
            rejected: job.counts.rejected + (action === 'reject' ? 1 : 0),
            incorrect: job.counts.incorrect + (action === 'incorrect' ? 1 : 0),
            reviewed: job.counts.reviewed + 1,
          },
        });
      }
      posthog?.capture('review_queue_decision', {
        ...feedbackContext(reviewedCandidate, job, tier),
        $insert_id: `review-queue-decision:${decision.id}`,
        action,
        rejection_reason: rejectionReason,
        decision_latency_ms: decisionLatencyMs,
      });
      if (action === 'reject') {
        const nextPrompt = { decision, candidate: reviewedCandidate, promptedAt: Date.now() };
        setReasonPrompt(nextPrompt);
        setReasonError('');
        posthog?.capture('review_rejection_reason_prompted', {
          ...feedbackContext(reviewedCandidate, job, tier),
          $insert_id: `review-rejection-reason-prompted:${decision.id}`,
          review_id: decision.id,
        });
      }
      if (mockGenerationEnabled && pending.length === 1) {
        setPhase('complete');
        setJob(previous => previous ? { ...previous, status: 'completed', stage: 'completed' } : previous);
      } else if (!mockGenerationEnabled) {
        window.setTimeout(() => refreshJob(reviewedCandidate.job_id), 100);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that decision');
    } finally {
      setSubmitting(false);
    }
  }, [current, job, pending.length, posthog, reasonPrompt, refreshJob, submitting, tier, mockGenerationEnabled]);

  const submitRejectionReason = useCallback(async (reason: RejectionReason) => {
    if (!reasonPrompt || reasonSubmitting) return;
    const prompt = reasonPrompt;
    const reasonLatencyMs = Math.max(0, Date.now() - prompt.promptedAt);
    setReasonSubmitting(true);
    setReasonError('');
    try {
      const decision = mockGenerationEnabled
        ? { ...prompt.decision, rejection_reason: reason }
        : await updateReviewReason(prompt.decision.id, reason, reasonLatencyMs);
      setLastReview(previous => previous?.decision.id === decision.id
        ? { ...previous, decision }
        : previous);
      posthog?.capture('review_rejection_reason_added', {
        ...feedbackContext(prompt.candidate, job, tier),
        $insert_id: `review-rejection-reason-added:${decision.id}:${reason}`,
        review_id: decision.id,
        rejection_reason: reason,
        reason_latency_ms: reasonLatencyMs,
      });
      setReasonPrompt(null);
    } catch (err) {
      captureError('review_rejection_reason_error', err, {
        job_id: prompt.candidate.job_id,
        candidate_id: prompt.candidate.id,
        rejection_reason: reason,
      });
      setReasonError('The rejection was saved, but the optional reason did not sync. Try again or skip it.');
    } finally {
      setReasonSubmitting(false);
    }
  }, [job, posthog, reasonPrompt, reasonSubmitting, tier, mockGenerationEnabled]);

  const dismissRejectionReason = useCallback((dismissalReason = 'skipped') => {
    if (!reasonPrompt || reasonSubmitting) return;
    posthog?.capture('review_rejection_reason_dismissed', {
      ...feedbackContext(reasonPrompt.candidate, job, tier),
      $insert_id: `review-rejection-reason-dismissed:${reasonPrompt.decision.id}`,
      review_id: reasonPrompt.decision.id,
      dismissal_reason: dismissalReason,
      reason_latency_ms: Math.max(0, Date.now() - reasonPrompt.promptedAt),
    });
    setReasonPrompt(null);
    setReasonError('');
  }, [job, posthog, reasonPrompt, reasonSubmitting, tier]);

  const saveEdit = useCallback(async () => {
    if (!current || !editCard || submitting) return;
    if (editCard.type === 'cloze') {
      const validationError = clozeEditError(editCard.clozeText || '');
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    setError('');
    setSubmitting(true);
    try {
      const original = versionAsCard(current);
      const diff: Record<string, unknown> = {};
      for (const key of ['front', 'back', 'clozeText', 'answer', 'hint'] as const) {
        if (original[key] !== editCard[key]) {
          diff[key] = { before: original[key] || null, after: editCard[key] || null };
        }
      }
      if (mockGenerationEnabled) {
        const versionId = uuid();
        const editedCandidate: ReviewCandidate = {
          ...current,
          current_version_id: versionId,
          current_version: {
            ...current.current_version,
            id: versionId,
            version_number: current.current_version.version_number + 1,
            created_by: 'user',
            front: editCard.front || null,
            back: editCard.back || null,
            cloze_text: normalizeClozeText(editCard.clozeText || '') || null,
            answer: editCard.answer || null,
            hint: editCard.hint || null,
            tags: editCard.tags || [],
          },
        };
        setSubmitting(false);
        await submitDecision('edit_accept', versionId, undefined, editedCandidate);
        return;
      }
      const version = await createReviewCardVersion(
        current,
        editCard,
        diff,
        normalizedEditDistance(original, editCard),
      );
      setSubmitting(false);
      await submitDecision('edit_accept', version.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save this edit');
      setSubmitting(false);
    }
  }, [current, editCard, submitDecision, submitting, mockGenerationEnabled]);

  const undo = useCallback(async () => {
    if (!lastReview || submitting) return;
    setError('');
    setSyncMessage('');
    setSubmitting(true);
    try {
      if (mockGenerationEnabled) {
        setPending(previous => [lastReview.candidate, ...previous]);
        if (lastReview.decision.action === 'accept' || lastReview.decision.action === 'edit_accept') {
          setMockAccepted(previous => previous.filter(candidate => candidate.id !== lastReview.candidate.id));
        }
        setJob(previous => {
          if (!previous) return previous;
          const action = lastReview.decision.action;
          return {
            ...previous,
            status: 'paused_buffer_full',
            stage: 'generating',
            counts: {
              ...previous.counts,
              pending: previous.counts.pending + 1,
              accepted: Math.max(0, previous.counts.accepted - (action === 'accept' || action === 'edit_accept' ? 1 : 0)),
              rejected: Math.max(0, previous.counts.rejected - (action === 'reject' ? 1 : 0)),
              incorrect: Math.max(0, previous.counts.incorrect - (action === 'incorrect' ? 1 : 0)),
              reviewed: Math.max(0, previous.counts.reviewed - 1),
            },
          };
        });
        setPhase('reviewing');
      } else {
        await undoReviewDecision(lastReview.decision.id);
      }
      if (reasonPrompt?.decision.id === lastReview.decision.id) {
        dismissRejectionReason('review_undone');
      }
      setLastReview(null);
      if (!mockGenerationEnabled && job) await refreshJob(job.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not undo the last decision');
    } finally {
      setSubmitting(false);
    }
  }, [dismissRejectionReason, job, lastReview, reasonPrompt, refreshJob, submitting, mockGenerationEnabled]);

  const loadAcceptedCards = useCallback(async (): Promise<FlashCard[]> => {
    if (!job) return [];
    const accepted = mockGenerationEnabled
      ? mockAccepted
      : await listReviewCandidates(job.id, 'accepted');
    return accepted.map(reviewCandidateToFlashCard);
  }, [job, mockAccepted, mockGenerationEnabled]);

  const exportAccepted = useCallback(async () => {
    if (!job || job.counts.accepted === 0 || exporting || exportInProgress) return;
    if (isDemo) {
      setPostExport({ jobId: job.id, acceptedCards: job.counts.accepted });
      setSyncMessage('Demo export complete — your reviewed deck is ready.');
      return;
    }
    setExporting(true);
    const exportStartedAt = Date.now();
    posthog?.capture('review_queue_export_started', {
      job_id: job.id,
      course_id: job.course_id,
      accepted_cards: job.counts.accepted,
      mock_generation: mockGenerationEnabled,
      workflow_schema_version: REVIEW_WORKFLOW_ANALYTICS_VERSION,
    });
    try {
      const deckName = fileName.replace(/\.[^.]+$/, '') || 'SlideToAnki Export';
      const cards = await runExport(async report => {
        const collected = await loadAcceptedCards();
        const blob = await exportToAnki(collected, deckName, undefined, report);
        downloadDeckPackage(blob, deckName);
        return collected;
      }, job.counts.accepted);
      posthog?.capture('review_queue_export_completed', {
        job_id: job.id,
        course_id: job.course_id,
        accepted_cards: cards.length,
        export_duration_ms: Date.now() - exportStartedAt,
        mock_generation: mockGenerationEnabled,
        workflow_schema_version: REVIEW_WORKFLOW_ANALYTICS_VERSION,
      });
      const nextExportCount = incrementExportCount();
      setPostExport({ jobId: job.id, acceptedCards: cards.length });
      if (nextExportCount >= 2 && !testimonialGiven) {
        window.setTimeout(() => {
          posthog?.capture('testimonial_prompt_shown', {
            job_id: job.id,
            export_count: nextExportCount,
            accepted_cards: cards.length,
            current_tier: tier,
          });
          setShowTestimonialModal(true);
        }, 1800);
      }
    } catch (err) {
      captureError('review_queue_export_error', err, {
        job_id: job.id,
        accepted_cards: job.counts.accepted,
      });
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [exporting, exportInProgress, runExport, fileName, incrementExportCount, job, loadAcceptedCards, posthog, testimonialGiven, tier, isDemo, mockGenerationEnabled]);

  const syncAccepted = useCallback(async () => {
    if (!job || job.counts.accepted === 0 || syncing) return;
    if (isDemo) { setSyncMessage('Demo: the kept cards would now be sent to Anki.'); return; }
    setSyncing(true);
    setSyncMessage('');
    posthog?.capture('anki_direct_sync_started', {
      job_id: job.id,
      course_id: job.course_id,
      accepted_cards: job.counts.accepted,
      mock_generation: mockGenerationEnabled,
      workflow_schema_version: REVIEW_WORKFLOW_ANALYTICS_VERSION,
    });
    try {
      const cards = await loadAcceptedCards();
      const deckName = fileName.replace(/\.[^.]+$/, '') || 'SlideToAnki Export';
      const hasImageOcclusion = cards.some(card => card.type === 'image_occlusion');
      const available = await isAnkiConnectAvailable();
      if (!available) {
        setSyncMessage('Open Anki desktop with AnkiConnect installed, then try again. You can always download the .apkg file.');
        posthog?.capture('anki_direct_sync_unavailable', {
          job_id: job.id,
          course_id: job.course_id,
          has_image_occlusion: hasImageOcclusion,
          accepted_cards: cards.length,
          mock_generation: mockGenerationEnabled,
          workflow_schema_version: REVIEW_WORKFLOW_ANALYTICS_VERSION,
        });
        return;
      }
      const result = await syncCardsToAnki(cards, deckName);
      setSyncMessage(`Deck sent to Anki (${result.imported} cards).`);
      posthog?.capture('anki_direct_sync_completed', {
        job_id: job.id,
        course_id: job.course_id,
        cards_imported: result.imported,
        mock_generation: mockGenerationEnabled,
        workflow_schema_version: REVIEW_WORKFLOW_ANALYTICS_VERSION,
      });
    } catch (err) {
      setSyncMessage(err instanceof Error ? err.message : 'Could not send the deck to Anki. Try downloading the .apkg file.');
      captureError('anki_direct_sync_error', err, { job_id: job.id });
    } finally {
      setSyncing(false);
    }
  }, [fileName, job, loadAcceptedCards, posthog, syncing, isDemo, mockGenerationEnabled]);

  const saveAcceptedToLibrary = useCallback(async () => {
    if (!job || job.counts.accepted === 0 || savingLibrary || savedLibrary) return;
    if (isDemo) { setSavedLibrary(true); return; }
    if (!user) {
      await openSignIn('/upload');
      return;
    }
    if (!canSaveToLibrary) {
      posthog?.capture('library_limit_gated', { tier, deck_count: deckCount, source: 'review_queue' });
      setUpgradeTrigger('library_limit');
      setShowUpgrade(true);
      return;
    }

    setSavingLibrary(true);
    setError('');
    posthog?.capture('review_queue_library_save_started', {
      job_id: job.id,
      accepted_cards: job.counts.accepted,
    });
    try {
      const accepted = mockGenerationEnabled
        ? mockAccepted
        : await listReviewCandidates(job.id, 'accepted');
      const cards = accepted.map(reviewCandidateToFlashCard);
      const originalFile = mockGenerationEnabled ? null : await getReviewSourceFile(job.id, fileName);
      await saveDeckToLibrary(user.id, fileName || 'SlideToAnki deck', cards, null, originalFile);
      setSavedLibrary(true);
      await refreshSubscription();
      posthog?.capture('review_queue_library_save_completed', {
        job_id: job.id,
        accepted_cards: cards.length,
      });
    } catch (err) {
      captureError('review_queue_library_save_error', err, {
        job_id: job.id,
        accepted_cards: job.counts.accepted,
      });
      if (err instanceof Error && err.message.includes('library_limit_reached')) {
        setUpgradeTrigger('library_limit');
        setShowUpgrade(true);
      } else {
        setError(err instanceof Error ? err.message : 'Could not save this deck');
      }
      posthog?.capture('review_queue_library_save_failed', {
        job_id: job.id,
        error_message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSavingLibrary(false);
    }
  }, [canSaveToLibrary, deckCount, fileName, job, mockAccepted, posthog, refreshSubscription, savedLibrary, savingLibrary, openSignIn, tier, user, isDemo, mockGenerationEnabled]);

  const startOver = useCallback(() => {
    reviewEpochRef.current += 1;
    persistActiveJob(null);
    setJob(null);
    setPending([]);
    setMockAccepted([]);
    setFileName('');
    setError('');
    setConnectionNotice('');
    pollErrorCapturedRef.current = false;
    consecutivePollFailuresRef.current = 0;
    pollDelayRef.current = FIRST_CARD_POLL_INTERVAL_MS;
    waitContextRef.current = null;
    factsFirstShownRef.current = null;
    setFactsPreviewDeadline(null);
    setWaitStartedAt(null);
    setLastReview(null);
    setEditing(false);
    setEditCard(null);
    setShowNewLectureDialog(false);
    setReasonPrompt(null);
    setReasonError('');
    setReasonSubmitting(false);
    setSavedLibrary(false);
    setCoverage(null);
    setSyncMessage('');
    setPostExport(null);
    setShowTestimonialModal(false);
    setPhase('idle');
  }, [persistActiveJob]);

  const startAnotherLecture = useCallback(async () => {
    if (submitting) return;
    // Leave the durable job and its review decisions intact. Generation will
    // pause when the old job's buffer fills.
    startOver();
    const url = new URL(window.location.href);
    for (const key of ['job', 'file', 'mockFile', 'course']) url.searchParams.delete(key);
    window.history.pushState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, [startOver, submitting]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      // The modal owns its number/Escape shortcuts; never review or undo a
      // different card while feedback for the rejected card is open.
      if (reasonPrompt || showNewLectureDialog) return;
      const target = event.target as HTMLElement | null;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) return;
      if (event.metaKey || event.ctrlKey) {
        if (event.key.toLowerCase() === 'z') {
          event.preventDefault();
          undo();
        }
        return;
      }
      if (phase === 'uploading' || phase === 'preparing') return;
      if (!current || submitting || editing) return;
      if (event.key.toLowerCase() === 'a' || event.key === 'ArrowRight') {
        event.preventDefault();
        submitDecision('accept');
      } else if (event.key.toLowerCase() === 'x' || event.key === 'ArrowLeft') {
        event.preventDefault();
        submitDecision('reject');
      } else if (event.key.toLowerCase() === 'e') {
        event.preventDefault();
        setEditing(true);
      } else if (event.key.toLowerCase() === 'i') {
        event.preventDefault();
        submitDecision('incorrect', undefined, 'incorrect');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [current, dismissRejectionReason, editing, phase, reasonPrompt, showNewLectureDialog, submitDecision, submitRejectionReason, submitting, undo, isDemo, mockGenerationEnabled, demoFileName]);

  const cardContent = useMemo(() => {
    if (!current) return null;
    const card = versionAsCard(current);
    if (card.type === 'cloze') {
      const text = card.clozeText || '';
      return { prompt: text, answer: null, image: null, isCloze: true };
    }
    if (card.type === 'image_occlusion') {
      return {
        prompt: card.hint || 'Which structure is hidden?',
        answer: card.answer || '',
        image: card.occludedImage || card.image || null,
        isCloze: false,
      };
    }
    return { prompt: card.front || '', answer: card.back || '', image: null, isCloze: false };
  }, [current]);

  useReviewBatchWaitAnalytics({
    context: job && !mockGenerationEnabled ? {
      job_id: job.id,
      stage: job.stage,
      status: job.status,
      user_tier: tier,
      source_slide_count: job.source_slide_count,
      requested_card_types: job.requested_card_types,
      reviewed_count: job.counts.reviewed,
      accepted_count: job.counts.accepted,
    } : null,
    showing: phase === 'reviewing' && !(current && cardContent),
    obscured: Boolean(reasonPrompt) || showNewLectureDialog || showUpgrade || showTestimonialModal,
    endReason: current && cardContent ? 'cards_ready'
      : job?.status === 'completed' ? 'generation_completed'
        : job?.status === 'cancelled' ? 'generation_cancelled'
          : job?.status === 'failed_terminal' || phase === 'error' ? 'generation_failed' : 'left_review',
  });

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-background to-card px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Review Queue</p>
            <h1 className="mt-1 font-serif text-3xl font-bold text-foreground sm:text-4xl">
              Build a deck worth studying
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {phase === 'reviewing' && (
              <button
                type="button"
                onClick={() => setShowNewLectureDialog(true)}
                disabled={submitting || editing || Boolean(reasonPrompt)}
                className="ui-button ui-button--secondary"
              >New lecture</button>
            )}
            {job && (phase === 'reviewing' || job.counts.reviewed > 0) && (
              <div className="rounded-full border border-border bg-card px-4 py-2 text-sm text-muted shadow-sm">
                <strong className="text-foreground">{job.counts.accepted} kept</strong>
                <span className="mx-2">·</span>
                {job.counts.reviewed} reviewed
              </div>
            )}
          </div>
        </div>

        {showNewLectureDialog && (
          <NewLectureDialog
            onCancel={() => setShowNewLectureDialog(false)}
            onConfirm={() => void startAnotherLecture()}
          />
        )}

        {error && phase !== 'error' && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{error}</span>
            <button onClick={() => setError('')} className="ui-button ui-button--ghost">Dismiss</button>
          </div>
        )}

        {connectionNotice && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {connectionNotice}
          </div>
        )}

        {reasonPrompt && (
          <RejectionReasonDialog
            key={reasonPrompt.decision.id}
            candidate={reasonPrompt.candidate}
            error={reasonError}
            submitting={reasonSubmitting}
            onChoose={submitRejectionReason}
            onSkip={() => dismissRejectionReason()}
          />
        )}

        {phase === 'idle' && (
          <UploadLecturePanel ready={Boolean(deviceId)} onFile={startJob} />
        )}

        {(phase === 'uploading' || phase === 'preparing') && (
          <GenerationWaitPanel
            phase={phase}
            job={job}
            fileName={fileName}
            sampleFacts={mockGenerationEnabled}
            readyForReview={pending.length > 0}
          />
        )}

        {phase === 'reviewing' && (
          <section>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-xs text-muted">
              <span>{pending.length} ready to review{queueRunningLow && pending.length > 0 && job && !['completed', 'cancelled', 'failed_terminal'].includes(job.status) ? ' · More cards are on the way' : ''}</span>
            </div>

            {current && cardContent ? <div className="rounded-3xl border border-border bg-white p-5 shadow-[0_18px_60px_rgba(40,35,25,0.09)] sm:p-6">
              <div className="mb-5 flex items-center justify-between gap-3 text-xs">
                <span className="rounded-full bg-primary-light px-3 py-1 font-semibold text-primary">
                  {current.card_type === 'basic' ? 'Q&A' : current.card_type === 'cloze' ? 'Cloze' : 'Image occlusion'}
                </span>
                {current.origin !== 'generated' && <span className="text-muted">
                  {current.origin === 'retrieved_exact'
                    ? 'Previously approved by you'
                    : 'Trusted card · re-grounded to this lecture'}
                </span>}
              </div>

              {editing && editCard ? (
                <div className="space-y-4">
                  {current.card_type === 'basic' && (
                    <>
                      <Field label="Question" value={editCard.front || ''} onChange={value => setEditCard({ ...editCard, front: value })} />
                      <Field label="Answer" value={editCard.back || ''} onChange={value => setEditCard({ ...editCard, back: value })} rows={5} />
                    </>
                  )}
                  {current.card_type === 'cloze' && (
                    <Field label="Cloze text" value={editCard.clozeText || ''} onChange={value => setEditCard({ ...editCard, clozeText: value })} rows={6} />
                  )}
                  {current.card_type === 'image_occlusion' && (
                    <>
                      <Field label="Answer" value={editCard.answer || ''} onChange={value => setEditCard({ ...editCard, answer: value })} />
                      <Field label="Hint" value={editCard.hint || ''} onChange={value => setEditCard({ ...editCard, hint: value })} />
                    </>
                  )}
                  <div className="flex justify-end gap-3 pt-2">
                    <button onClick={() => setEditing(false)} className="ui-button ui-button--secondary">Cancel</button>
                    <button onClick={saveEdit} disabled={submitting} className="ui-button ui-button--primary">
                      Save and keep
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="flex min-h-[220px] w-full flex-col items-center justify-center rounded-2xl bg-surface px-5 py-6 text-left sm:min-h-[clamp(220px,32vh,360px)] sm:px-8"
                >
                  {cardContent.image && (
                    <img
                      src={cardContent.image}
                      alt="Medical diagram with one label occluded"
                      className="mb-7 max-h-[420px] max-w-full rounded-xl border border-border object-contain"
                    />
                  )}
                  <p className="w-full whitespace-pre-wrap text-center font-serif text-xl leading-9 text-foreground sm:text-2xl">
                    {cardContent.isCloze
                      ? <ClozeText text={cardContent.prompt} />
                      : cardContent.prompt}
                  </p>
                  {cardContent.answer && (
                    <div className="mt-8 w-full border-t border-border pt-7 text-center text-base leading-7 text-foreground sm:text-lg">
                      {cardContent.answer}
                    </div>
                  )}
                  {current.source && (
                    <details
                      className="mt-8 w-full rounded-xl border border-border bg-card px-4 py-3 text-left"
                      onToggle={event => {
                        if (!event.currentTarget.open) return;
                        posthog?.capture('review_source_evidence_opened', {
                          $insert_id: `review-source-evidence-opened:${current.id}`,
                          job_id: current.job_id,
                          course_id: job?.course_id,
                          candidate_id: current.id,
                          card_type: current.card_type,
                          candidate_origin: current.origin,
                          slide_number: current.source?.slide_number,
                          mock_generation: mockGenerationEnabled,
                          workflow_schema_version: REVIEW_WORKFLOW_ANALYTICS_VERSION,
                        });
                      }}
                    >
                      <summary className="cursor-pointer text-sm font-semibold text-primary">
                        Supported by slide {current.source.slide_number}
                      </summary>
                      <p className="mt-2 whitespace-pre-wrap text-xs leading-5 text-muted">{current.source.excerpt}</p>
                    </details>
                  )}
                </div>
              )}
            </div> : <QueueRefillPanel stage={job?.stage} status={job?.status} reviewedCount={job?.counts.reviewed || 0} acceptedCount={job?.counts.accepted || 0} />}

            {!editing && current && (
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <ActionButton label="Reject" shortcut="X / ←" tone="neutral" disabled={submitting} onClick={() => submitDecision('reject')} />
                <ActionButton label="Edit" shortcut="E" tone="neutral" disabled={submitting} onClick={() => setEditing(true)} />
                <ActionButton label="Keep" shortcut="A / →" tone="primary" disabled={submitting} onClick={() => submitDecision('accept')} />
                <ActionButton label="Incorrect" shortcut="I" tone="danger" disabled={submitting} onClick={() => submitDecision('incorrect', undefined, 'incorrect')} />
              </div>
            )}

          </section>
        )}

        {phase === 'complete' && (
          <DeckCompletionPanel description={<>
              You kept {job?.counts.accepted || 0} card{job?.counts.accepted === 1 ? '' : 's'} and reviewed {job?.counts.reviewed || 0} candidate{job?.counts.reviewed === 1 ? '' : 's'}.
            </>}>
            <div className="mx-auto mt-6 max-w-2xl text-left"><CoveragePanel coverage={coverage} /></div>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <button onClick={startAnotherLecture} disabled={submitting} className="ui-button ui-button--secondary">Start another lecture</button>
              <DeckActions onDownload={exportAccepted} onSync={syncAccepted} onSave={saveAcceptedToLibrary}
                busy={!job?.counts.accepted} active={exporting ? 'download' : syncing ? 'sync' : savingLibrary ? 'save' : null}
                saved={savedLibrary} />
            </div>
            {syncMessage && <p className="mt-4 text-sm font-medium text-muted" role="status">{syncMessage}</p>}
            {postExport && job && postExport.jobId === job.id && (
              <PostExportGrowthPanel
                demo={isDemo}
                jobId={job.id}
                courseId={job.course_id}
                fileName={fileName}
                acceptedCards={postExport.acceptedCards}
                tier={tier}
                generationsRemaining={generationsRemaining}
                onStartAnother={() => void startAnotherLecture()}
                onShowUpgrade={() => {
                  setUpgradeTrigger('post_export');
                  setShowUpgrade(true);
                }}
                onReferralUpdated={refreshSubscription}
              />
            )}
          </DeckCompletionPanel>
        )}

        {phase === 'error' && (
          <section className="rounded-3xl border border-red-200 bg-white px-6 py-12 text-center shadow-sm">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-2xl text-red-600">!</div>
            <h2 className="font-serif text-2xl font-semibold">We could not continue</h2>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-muted">{error}</p>
            {!!job?.counts.accepted && <div className="mt-5 flex justify-center"><DeckActions onDownload={exportAccepted} onSync={syncAccepted} onSave={saveAcceptedToLibrary}
              active={exporting ? 'download' : syncing ? 'sync' : savingLibrary ? 'save' : null} saved={savedLibrary}/></div>}
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              {!user && (
                <button onClick={() => openSignIn('/upload')} className="ui-button ui-button--primary">Sign in with Google</button>
              )}
              <button onClick={startAnotherLecture} className="ui-button ui-button--secondary">Try another file</button>
            </div>
          </section>
        )}
      </div>

      {showTestimonialModal && (
        <TestimonialModal
          deviceId={deviceId}
          sourceJobId={job?.id}
          onDismiss={() => {
            posthog?.capture('testimonial_prompt_dismissed', { job_id: job?.id, current_tier: tier });
            setShowTestimonialModal(false);
          }}
          onSubmitted={() => {
            setTestimonialGiven();
            setShowTestimonialModal(false);
          }}
        />
      )}
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger={upgradeTrigger} />
    </main>
  );
}

function CoveragePanel({ coverage }: { coverage: ReviewCoverage | null }) {
  if (!coverage) {
    return (
      <div className="mt-5 rounded-2xl border border-border bg-card p-5 text-sm text-muted" role="status">
        Mapping cards back to the lecture…
      </div>
    );
  }
  return (
    <section className="mt-5 rounded-2xl border border-border bg-card p-5" aria-label="Lecture coverage">
      <h3 className="font-serif text-lg font-semibold text-foreground">Lecture coverage</h3>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-xl bg-surface p-3"><strong className="block text-lg text-foreground">{coverage.represented_slides}</strong><span className="text-muted">slides represented</span></div>
        <div className="rounded-xl bg-surface p-3"><strong className="block text-lg text-foreground">{coverage.planned_facts}</strong><span className="text-muted">facts selected</span></div>
        <div className="rounded-xl bg-surface p-3"><strong className="block text-lg text-foreground">{coverage.accepted}</strong><span className="text-muted">cards kept</span></div>
      </div>
      <div className="mt-4 max-h-64 space-y-2 overflow-y-auto pr-1">
        {coverage.slides.map(slide => (
          <details key={slide.slide_number} className="rounded-xl border border-border bg-surface px-3 py-2">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-xs font-semibold text-foreground">
              <span>Slide {slide.slide_number}</span>
              <span className="text-muted">{slide.candidates} card{slide.candidates === 1 ? '' : 's'} · {slide.accepted} kept</span>
            </summary>
            <p className="mt-2 whitespace-pre-wrap text-xs leading-5 text-muted">{slide.excerpt}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

export function GenerationWaitPanel({
  phase,
  job,
  fileName,
  sampleFacts = false,
  readyForReview = false,
}: {
  phase: 'uploading' | 'preparing';
  job: Pick<ReviewGenerationJob, 'status' | 'stage' | 'text_parse_completed_at' | 'planning_completed_at' | 'first_candidate_ready_at' | 'source_slide_count' | 'selected_facts' | 'card_estimate'> & Partial<Pick<ReviewGenerationJob, 'requested_card_types'>> | null;
  fileName: string;
  sampleFacts?: boolean;
  readyForReview?: boolean;
}) {
  const uploadComplete = phase === 'preparing' || Boolean(job);
  const textComplete = Boolean(job?.text_parse_completed_at);
  const planningComplete = Boolean(job?.planning_completed_at);
  const firstCardComplete = Boolean(job?.first_candidate_ready_at);
  const facts = textComplete ? (job?.selected_facts ?? []).slice(0, 3) : [];
  return <GenerationWaitPanelView
    fileName={fileName}
    heading={readyForReview ? 'Your first cards are ready' : phase === 'uploading' ? 'Saving your lecture…' : stageLabel(job)}
    uploadComplete={uploadComplete} textComplete={textComplete}
    planningComplete={planningComplete} firstCardComplete={firstCardComplete}
    sourceCount={job?.source_slide_count} facts={facts} sampleFacts={sampleFacts}
  />;
}

function Field({ label, value, onChange, rows = 3 }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold">{label}</span>
      <textarea
        aria-label={label}
        value={value}
        rows={rows}
        onChange={event => onChange(event.target.value)}
        className="w-full rounded-xl border border-border bg-white px-4 py-3 leading-6 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10"
      />
    </label>
  );
}

function ActionButton({ label, shortcut, tone, disabled, onClick }: {
  label: string;
  shortcut: string;
  tone: 'neutral' | 'primary' | 'danger';
  disabled: boolean;
  onClick: () => void;
}) {
  const classes = tone === 'primary'
    ? 'border-primary bg-primary text-white'
    : tone === 'danger'
      ? 'border-red-200 bg-red-50 text-red-700'
      : 'border-border bg-white text-foreground';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`rounded-xl border px-4 py-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow disabled:opacity-40 ${classes}`}
    >
      <span className="block font-semibold">{label}</span>
      <span className={`mt-0.5 block text-[11px] ${tone === 'primary' ? 'text-white/75' : 'text-muted'}`}>{shortcut}</span>
    </button>
  );
}
